// Extracts a human-readable error string from a DRF-style API response.
//
// Backend error shapes vary:
//   {"detail": "..."}                          — permission/404/DRF exceptions
//   {"message": "..."}                          — some legacy endpoints
//   {"non_field_errors": ["..."]}               — serializer-level validation
//   {"field_name": ["error 1", "error 2"]}      — field-level validation
//   "plain string"                              — rare, some legacy endpoints
//
// Accepts either the full callApi() result ({ok, status, data, error}) or a
// bare data object, so callers don't need to unwrap it first.
export function extractApiError(res, fallback = 'Something went wrong') {
    if (res == null) return fallback;

    const data = (typeof res === 'object' && 'data' in res) ? res.data : res;

    if (data == null) {
        // callApi() sets `error` (a string, e.g. network failure) when there's no response data.
        if (typeof res === 'object' && typeof res.error === 'string' && res.error) {
            return res.error;
        }
        return fallback;
    }

    if (typeof data === 'string') {
        return data || fallback;
    }

    if (typeof data !== 'object') {
        return fallback;
    }

    if (typeof data.detail === 'string' && data.detail) {
        return data.detail;
    }

    if (typeof data.message === 'string' && data.message) {
        return data.message;
    }

    if (Array.isArray(data.non_field_errors) && data.non_field_errors.length > 0) {
        return String(data.non_field_errors[0]);
    }

    // First field-level error, formatted as "field: message".
    const IGNORED_KEYS = new Set(['detail', 'message', 'non_field_errors', 'code', 'channels', 'fields']);
    // Prefer the `fields` bucket the payment-link command contract nests field errors under.
    const fieldSources = [];
    if (data.fields && typeof data.fields === 'object') fieldSources.push(data.fields);
    fieldSources.push(data);

    for (let i = 0; i < fieldSources.length; i += 1) {
        const source = fieldSources[i];
        const entries = Object.entries(source).filter(([field]) => !IGNORED_KEYS.has(field));
        for (let j = 0; j < entries.length; j += 1) {
            const [field, value] = entries[j];
            if (Array.isArray(value) && value.length > 0) {
                return `${field}: ${value[0]}`;
            }
            if (typeof value === 'string' && value) {
                return `${field}: ${value}`;
            }
        }
    }

    return fallback;
}

export default extractApiError;
